// src/pages/UserPage/sections/OrganizationDeliveries/netvisor/invoicePreviewHelpers.js
//
// Shared helpers for the Netvisor preview.
//
// `formatAddress` and `buildInvoiceLineText` mirror `createInvoiceXML` in
//   services/netvisor/invoice.service.js
// so the preview shows the exact `salesInvoiceProductLineFreeText` that will be
// sent. KEEP IN SYNC with the backend — including the per-yNumber reference
// branches (their current ordering is reproduced as-is so the preview matches
// reality rather than a cleaned-up version).

export const fmtEur = (n) => (n == null ? '—' : `${Number(n).toFixed(2)} €`);

export const formatAddress = (address, maxSegments = 2) =>
  String(address || '')
    .split(',')
    .map((seg) => seg.trim())
    .slice(0, maxSegments)
    .join(', ');

export const buildInvoiceLineText = (d, yNumber) => {
  const ms = d.targetTime ?? d.completedAtMs;
  const completedDate = ms ? new Date(ms).toISOString().split('T')[0] : '';
  const fromAddress = formatAddress(d.from || d.origin);
  const toAddress   = formatAddress(d.to || d.destination);

  let freeText =
    `Mistä: ${fromAddress}, Mihin: ${toAddress}, ` +
    `Lähetystunnus: ${d.deliveryId || d.id}, ` +
    `Toimitusaika: ${completedDate}`;

  if (d.contractId) {
    freeText += `, Sopimustunnus: ${d.contractId}`;
  }

  if (d.customerReference || d?.billing?.paymentReference || yNumber === '2844561-8' || yNumber === '0313471-7') {
    const referencePayment = d.customerReference || d?.billing?.paymentReference || '';
    if (yNumber === '2862688-1') {
      freeText += `, Viite: ${referencePayment || ''}, 400`;
    } else if (yNumber === '0313471-7') {
      freeText += `, Viite: ${referencePayment || ''}, Tilaaja: Salonen Marja`;
    } else if (yNumber === '2844561-8') {
      freeText += `, Viite: ${referencePayment}, (MET 31232000145), Tilaaja: Tiina Vilen / Regea Kudospankki`;
    } else {
      freeText += `, Viite: ${referencePayment}`;
    }
  }

  return freeText.replace(/&/g, '');
};

// Newest-first, matching the backend's sort within createInvoiceXML.
export const byTargetTimeDesc = (a, b) =>
  new Date(b.targetTime ?? b.completedAtMs ?? 0) - new Date(a.targetTime ?? a.completedAtMs ?? 0);

// ── Billing resolution ───────────────────────────────────────────────────────
// In THIS app `noBilling` is a member-level flag (member.billing.noBilling),
// unlike the CMS where it lives on the delivery. Keep that distinction in mind
// when porting code between the two.

export const resolveBilling = (d, members) => {
  const member = members.find((m) => m.userId === d.senderId);
  return {
    member,
    noBilling:   member?.billing?.noBilling === true,
    yNumber:     d.billing?.yNumber     || member?.billing?.yNumber     || null,
    companyName: d.billing?.companyName || member?.billing?.companyName || member?.name || d.senderName || '',
  };
};

/**
 * Split a selection into: ready-to-send groups (one per Y-number, mirroring the
 * backend's one-invoice-per-Y-number rule), rows needing a Y-number, and rows
 * intentionally skipped.
 */
export const buildNetvisorSelection = (selectedDeliveries, members) => {
  const eligible = [];
  const needsFix = [];
  let skippedCount = 0;

  for (const d of selectedDeliveries) {
    const { noBilling, yNumber } = resolveBilling(d, members);
    if (noBilling) { skippedCount += 1; continue; }
    (yNumber ? eligible : needsFix).push(d);
  }

  const byY = {};
  for (const d of eligible) {
    const { yNumber, companyName } = resolveBilling(d, members);
    const key = yNumber || d.senderId || 'unknown';
    if (!byY[key]) byY[key] = { yNumber, companyName: companyName || '—', deliveries: [] };
    byY[key].deliveries.push(d);
  }

  const groups = Object.values(byY).map((g) => ({
    ...g,
    subtotal: g.deliveries.reduce((s, d) => s + (Number(d.price) || 0), 0),
  }));

  return { groups, eligible, needsFix, skippedCount };
};

// ── Local (in-memory) per-row overrides for the ready-to-send preview ───────
// Shape: { [deliveryId]: { freeText?: string, price?: string } }
// `price` is kept as the raw input string so typing "12." doesn't fight the
// user; it's parsed on read.

export const getEffectiveFreeText = (d, yNumber, overrides) => {
  const o = overrides?.[d.id];
  return o?.freeText !== undefined ? o.freeText : buildInvoiceLineText(d, yNumber);
};

export const getEffectivePrice = (d, overrides) => {
  const o = overrides?.[d.id];
  if (o?.price !== undefined && String(o.price).trim() !== '') {
    const n = Number(String(o.price).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }
  return Number(d.price) || 0;
};

export const isOverridden = (d, overrides) => {
  const o = overrides?.[d.id];
  return !!o && (o.freeText !== undefined || (o.price !== undefined && String(o.price).trim() !== ''));
};

// Firestore batches cap at 500 writes; chunk to stay safely under.
export const BATCH_LIMIT = 450;
export const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};